import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import { ActionButton } from '../forms';

/**
 * Modal component with semantic styling and configuration support
 * Supports Escape key to close, click outside to close, and customizable buttons
 */
const Modal = ({
  title,
  children,
  onClose,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonClass = '',
  showCancelButton = true,
  size = 'md',
  showCloseButton = true,
  open = true, // Support open prop for backward compatibility
  ...props
}) => {
  const colors = useColors();
  const semantic = colors.semantic;
  const { replace } = useTextReplacement();

  // Get portal target synchronously - prefer dedicated modal-root, fallback to body
  const modalRoot = useMemo(() => {
    if (typeof document !== 'undefined') {
      return document.getElementById('modal-root') || document.body;
    }
    return null;
  }, []);

  // Close modal when Escape key is pressed
  // Must be called before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    if (!open) {
      return; // Don't set up listener if modal is closed
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // If open is false, don't render the modal
  if (!open) {
    return null;
  }

  // Prevent clicks inside the modal from closing it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full',
  };

  // Default confirm button classes using semantic colors
  const defaultConfirmClass = 'cta-button-primary'; // Custom class from CSS
  const finalConfirmClass = confirmButtonClass || defaultConfirmClass;

  // Extract className from props to avoid overriding critical positioning
  const { className: backdropClassName, ...restProps } = props;

  // Modal content
  const modalContent = (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300] p-4 ${backdropClassName || ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      {...restProps}
    >
      <div
        className={`${semantic.contentCardBg} ${semantic.contentCardBorder} rounded-lg shadow-xl ${sizeClasses[size]} max-h-[90vh] flex flex-col`}
        style={{ width: '100%', maxWidth: '100%' }}
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className={`flex justify-between items-center p-4 border-b ${semantic.contentCardBorder}`}>
          <h3 id="modal-title" className={`text-lg font-medium ${semantic.contentCardHeading}`}>
            {replace(title)}
          </h3>
          {showCloseButton && (
            <ActionButton
              action="close"
              onClick={onClose}
              title="Close"
              size="sm"
            />
          )}
        </div>

        {/* Content */}
        <div className={`overflow-y-auto flex-grow p-4 ${semantic.contentCardText}`}>
          {children}
        </div>

        {/* Footer */}
        {(onConfirm || showCancelButton) && (
          <div className={`p-4 border-t ${semantic.contentCardBorder} flex justify-end gap-2`}>
            {showCancelButton && (
              <button
                onClick={onClose}
                className={`px-4 py-2 ${semantic.buttonSecondary} rounded-md transition-colors`}
              >
                {replace(cancelText)}
              </button>
            )}
            {onConfirm && (
              <button
                onClick={onConfirm}
                className={`px-4 py-2 rounded-md transition-colors ${finalConfirmClass}`}
              >
                {replace(confirmText)}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Use portal if available, otherwise render normally (for SSR compatibility)
  // Always use portal if modalRoot is available
  if (modalRoot) {
    return createPortal(modalContent, modalRoot);
  }

  // Fallback: render directly (shouldn't happen in browser, but safe for SSR)
  return modalContent;
};

Modal.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmButtonClass: PropTypes.string,
  showCancelButton: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', 'full']),
  showCloseButton: PropTypes.bool,
  open: PropTypes.bool, // Support open prop for backward compatibility
};

export default Modal;

